import { useState, useEffect, useCallback, useRef } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Button,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Snackbar,
  Paper,
  Box,
  Divider,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { Page, Header, HeaderLabel, Content } from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';

// ── Styles ────────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // Welcome / first-time screen
    welcomeRoot: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      gap: theme.spacing(3),
      padding: theme.spacing(4),
      textAlign: 'center',
    },
    welcomeIcon: {
      fontSize: 64,
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(1),
    },
    welcomeCard: {
      maxWidth: 560,
      width: '100%',
      padding: theme.spacing(4),
      borderRadius: theme.shape.borderRadius * 2,
      border: `1px solid ${theme.palette.divider}`,
    },
    stepList: {
      textAlign: 'left',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      '& li': {
        marginBottom: theme.spacing(1),
        color: theme.palette.text.secondary,
      },
    },
    // MLflow iframe view
    iframeRoot: {
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 200px)',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden',
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      padding: theme.spacing(1, 2),
      background: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
    },
    toolbarTitle: {
      flex: 1,
      fontWeight: 600,
      fontSize: '14px',
      color: theme.palette.text.secondary,
    },
    iframe: {
      flex: 1,
      border: 'none',
      width: '100%',
    },
    centred: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      gap: theme.spacing(2),
      flexDirection: 'column',
    },
    // Warning banner
    expiryBanner: {
      background: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
      padding: theme.spacing(0.75, 2),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      fontSize: '13px',
      flexShrink: 0,
    },
    // Token management panel
    tokenPanel: {
      padding: theme.spacing(3),
      maxWidth: 900,
    },
    tokenBox: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      background: '#1e1e1e',
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5, 2),
      fontFamily: 'monospace',
      fontSize: '13px',
      color: '#d4d4d4',
      wordBreak: 'break-all',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      border: '1px solid #333',
    },
    codeSnippet: {
      background: '#1e1e1e',
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#a8ff78',
      whiteSpace: 'pre',
      overflowX: 'auto',
      marginTop: theme.spacing(1),
      border: '1px solid #333',
      position: 'relative',
    },
    copySnippetBtn: {
      position: 'absolute',
      top: theme.spacing(0.5),
      right: theme.spacing(0.5),
      color: '#aaa',
      background: 'rgba(255,255,255,0.08)',
      borderRadius: 4,
      '&:hover': {
        background: 'rgba(255,255,255,0.18)',
        color: '#fff',
      },
    },
    sectionTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(3),
    },
    successChip: {
      background: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
  }),
);

// ── Types ─────────────────────────────────────────────────────────────────────
interface TokenRecord {
  id: string;
  label: string;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
}

interface NewTokenResult {
  id: string;
  token: string;
  label: string;
  createdAt: string;
  expiresAt: string | null;
}

const OWNER_GROUP_REF = 'group:default/code4ev-owners';

type PageView =
  | 'loading'       // initial load — checking tokens + granting session
  | 'first-time'    // no tokens exist → show welcome/generate screen
  | 'mlflow'        // has tokens + session granted → show iframe
  | 'tokens'        // manage tokens panel (reachable from toolbar)
  | 'error';        // session grant failed

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (d: string | null) =>
  d ? new Date(d).toLocaleString() : '—';

const isExpired = (d: string | null) =>
  d ? new Date(d) < new Date() : false;

const daysUntilExpiry = (d: string | null): number | null => {
  if (!d) return null;
  const diff = new Date(d).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

// ── Main Component ────────────────────────────────────────────────────────────
export const AiTrainerPage = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);

  const [view, setView] = useState<PageView>('loading');
  const [tokens, setTokens] = useState<TokenRecord[]>([]);

  // Create-token dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState<NewTokenResult | null>(null);

  // Owner-only permanent experiment purge dialog
  const [isOwner, setIsOwner] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeExperimentId, setPurgeExperimentId] = useState('');
  const [purgeConfirmation, setPurgeConfirmation] = useState('');
  const [purging, setPurging] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Session auto-refresh interval ref
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const mlflowUiUrl = `${window.location.origin}/mlflow`;
  const baseUrl = '/api/mlflow-bridge';

  // ── Get auth header ────────────────────────────────────────────────────────
  const authHeaders = useCallback(async (): Promise<HeadersInit> => {
    const { token } = await identityApi.getCredentials();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [identityApi]);

  // ── Grant browser session cookie ───────────────────────────────────────────
  const grantSession = useCallback(async (): Promise<boolean> => {
    try {
      const headers = await authHeaders();
      const resp = await fetch(`${baseUrl}/grant-session`, {
        credentials: 'include',
        headers,
      });
      return resp.ok;
    } catch {
      return false;
    }
  }, [authHeaders, baseUrl]);

  // ── Fetch token list ───────────────────────────────────────────────────────
  const fetchTokens = useCallback(async (): Promise<TokenRecord[]> => {
    try {
      const headers = await authHeaders();
      const resp = await fetch(`${baseUrl}/tokens`, { headers });
      if (resp.ok) {
        const list: TokenRecord[] = await resp.json();
        setTokens(list);
        return list;
      }
    } catch {
      // ignore
    }
    return [];
  }, [authHeaders, baseUrl]);

  // Visibility only: the backend also verifies owner membership before purge.
  useEffect(() => {
    let cancelled = false;
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (!cancelled) {
          setIsOwner(
            (identity.ownershipEntityRefs as string[] | undefined)?.includes(
              OWNER_GROUP_REF,
            ) ?? false,
          );
        }
      })
      .catch(() => {
        if (!cancelled) setIsOwner(false);
      });
    return () => { cancelled = true; };
  }, [identityApi]);

  // ── Initial load: check tokens + grant session ─────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [list, sessionOk] = await Promise.all([
        fetchTokens(),
        grantSession(),
      ]);

      if (cancelled) return;

      if (!sessionOk) {
        setView('error');
        return;
      }

      // Active tokens = not expired
      const activeTokens = list.filter(t => !isExpired(t.expires_at));
      setView(activeTokens.length === 0 ? 'first-time' : 'mlflow');
    })();
    return () => { cancelled = true; };
  }, [fetchTokens, grantSession]);

  // ── Silent session refresh every 7 hours ──────────────────────────────────
  useEffect(() => {
    refreshRef.current = setInterval(() => {
      grantSession();
    }, 7 * 60 * 60 * 1000);
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [grantSession]);

  // ── Create token ───────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      const headers = await authHeaders();
      const resp = await fetch(`${baseUrl}/tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          label: newLabel.trim(),
          expiresInDays: expiresInDays
            ? parseInt(expiresInDays, 10)
            : undefined,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        setSnackbar(`Error: ${err.error}`);
        return;
      }
      const result: NewTokenResult = await resp.json();
      setNewToken(result);
      await fetchTokens();
    } catch (e: any) {
      setSnackbar(`Error: ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  // ── After token created on first-time screen → go to MLflow ───────────────
  const handleFirstTokenDone = async () => {
    setCreateOpen(false);
    setNewLabel('');
    setExpiresInDays('');
    setNewToken(null);
    // Session was already granted on page load; just switch view
    setView('mlflow');
  };

  // ── Revoke token ───────────────────────────────────────────────────────────
  const handleRevoke = async (id: string, label: string) => {
    try {
      const headers = await authHeaders();
      const resp = await fetch(`${baseUrl}/tokens/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (resp.ok) {
        setSnackbar(`Token "${label}" revoked`);
        const updated = await fetchTokens();
        // If user revoked their last active token, go back to first-time screen
        const stillActive = updated.filter(t => !isExpired(t.expires_at));
        if (stillActive.length === 0) setView('first-time');
      } else {
        setSnackbar('Failed to revoke token');
      }
    } catch (e: any) {
      setSnackbar(`Error: ${e.message}`);
    }
  };

  const closePurgeDialog = () => {
    if (purging) return;
    setPurgeOpen(false);
    setPurgeExperimentId('');
    setPurgeConfirmation('');
  };

  const handlePurgeExperiment = async () => {
    const experimentId = purgeExperimentId.trim();
    if (!/^\d+$/.test(experimentId) || purgeConfirmation !== `PURGE ${experimentId}`) {
      return;
    }

    setPurging(true);
    try {
      const headers = await authHeaders();
      const response = await fetch(`${baseUrl}/experiments/purge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ experimentIds: [experimentId] }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error ?? 'Permanent experiment purge failed');
      }
      setSnackbar(`Experiment ${experimentId} was permanently deleted`);
      setPurgeOpen(false);
      setPurgeExperimentId('');
      setPurgeConfirmation('');
    } catch (error: any) {
      setSnackbar(`Purge failed: ${error.message}`);
    } finally {
      setPurging(false);
    }
  };

  // ── Copy to clipboard ──────────────────────────────────────────────────────
  const copy = (text: string, label = 'Copied to clipboard') => {
    // Clipboard API requires HTTPS; fall back to execCommand for HTTP deployments
    const execCommandFallback = () => {
      try {
        // Append inside the active dialog (if any) to bypass focus-trap restrictions
        const container = document.querySelector('[role="dialog"]') ?? document.body;
        const el = document.createElement('textarea');
        el.value = text;
        el.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
        container.appendChild(el);
        el.focus();
        el.select();
        el.setSelectionRange(0, text.length);
        const ok = document.execCommand('copy');
        container.removeChild(el);
        setSnackbar(ok ? label : 'Copy failed. Please copy manually.');
      } catch {
        setSnackbar('Copy failed. Please copy manually.');
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        () => setSnackbar(label),
        () => execCommandFallback(),
      );
    } else {
      execCommandFallback();
    }
  };

  // ── Expiry warning (soonest expiring active token) ─────────────────────────
  const soonestExpiry = tokens
    .filter(t => t.expires_at && !isExpired(t.expires_at))
    .sort(
      (a, b) =>
        new Date(a.expires_at!).getTime() - new Date(b.expires_at!).getTime(),
    )[0];
  const expiryDays = soonestExpiry
    ? daysUntilExpiry(soonestExpiry.expires_at)
    : null;
  const showExpiryWarning =
    expiryDays !== null && expiryDays <= 7 && expiryDays >= 0;

  // ── Python snippet builder ─────────────────────────────────────────────────

  const pythonSnippet = (token: string) =>
    `import os
  import mlflow

  # Set your token (or export it in your shell / CI environment)
  # The MLflow SDK appends /api/2.0/mlflow automatically — point to root only!
  os.environ["MLFLOW_TRACKING_TOKEN"] = "${token}"

  mlflow.set_tracking_uri("${window.location.origin}")
  mlflow.set_experiment("my-experiment")

  with mlflow.start_run():
      mlflow.log_param("learning_rate", 0.01)
      mlflow.log_metric("accuracy", 0.95)
      # mlflow.log_artifact("model.pkl")`;

  const genericSnippet = `import os
  import mlflow

  # Paste your token here or set it as an environment variable:
  #   export MLFLOW_TRACKING_TOKEN="mlf_your_token_here"
  os.environ["MLFLOW_TRACKING_TOKEN"] = "mlf_your_token_here"

  # The MLflow SDK appends /api/2.0/mlflow automatically — point to root only!
  mlflow.set_tracking_uri("${window.location.origin}")
  mlflow.set_experiment("my-experiment")

  with mlflow.start_run():
      mlflow.log_param("learning_rate", 0.01)
      mlflow.log_metric("accuracy", 0.95)`;

  // ── Render: Loading ────────────────────────────────────────────────────────
  if (view === 'loading') {
    return (
      <Page themeId="tool">
        <Header title="AI Trainer" subtitle="MLflow Experiment Tracking">
          <HeaderLabel label="Environment" value="MLflow" />
        </Header>
        <Content>
          <div className={classes.centred} style={{ minHeight: '60vh' }}>
            <CircularProgress />
            <Typography color="textSecondary">
              Starting AI Trainer…
            </Typography>
          </div>
        </Content>
      </Page>
    );
  }

  // ── Render: Error ──────────────────────────────────────────────────────────
  if (view === 'error') {
    return (
      <Page themeId="tool">
        <Header title="AI Trainer" subtitle="MLflow Experiment Tracking">
          <HeaderLabel label="Environment" value="MLflow" />
        </Header>
        <Content>
          <div className={classes.centred} style={{ minHeight: '60vh' }}>
            <Typography color="error" variant="h6">
              Could not establish a session
            </Typography>
            <Typography color="textSecondary">
              Please reload the page or log in again.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
          </div>
        </Content>
      </Page>
    );
  }

  // ── Render: First-time welcome screen ─────────────────────────────────────
  if (view === 'first-time') {
    return (
      <Page themeId="tool">
        <Header title="AI Trainer" subtitle="MLflow Experiment Tracking">
          <HeaderLabel label="Environment" value="MLflow" />
        </Header>
        <Content>
          <div className={classes.welcomeRoot}>
            <VpnKeyIcon className={classes.welcomeIcon} />
            <Paper className={classes.welcomeCard} elevation={2}>
              <Typography variant="h5" gutterBottom>
                Welcome to AI Trainer
              </Typography>
              <Typography color="textSecondary" variant="body2">
                To connect your local Python scripts or CI pipelines to this
                MLflow instance, you need a <strong>Personal API Token</strong>.
                This works exactly like a GitLab Personal Access Token — generate
                it once and reuse it everywhere.
              </Typography>
              <ul className={classes.stepList}>
                <li>Generate a token below (takes 5 seconds)</li>
                <li>
                  Copy the token and set{' '}
                  <code>MLFLOW_TRACKING_TOKEN</code> in your script
                </li>
                <li>
                  Point your script at{' '}
                  <code>{window.location.origin}/api/2.0/mlflow</code>
                </li>
                <li>
                  The MLflow UI opens automatically — no token needed in the
                  browser
                </li>
              </ul>
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                startIcon={<VpnKeyIcon />}
                onClick={() => setCreateOpen(true)}
              >
                Generate My API Token
              </Button>
            </Paper>
          </div>

          {/* Create Token Dialog — first-time variant */}
          <Dialog
            open={createOpen}
            onClose={() => {
              if (newToken) handleFirstTokenDone();
              else {
                setCreateOpen(false);
                setNewLabel('');
                setExpiresInDays('');
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {newToken ? 'Token Created' : 'Generate API Token'}
            </DialogTitle>
            <DialogContent>
              {!newToken ? (
                <>
                  <TextField
                    autoFocus
                    label="Token label"
                    placeholder="e.g. My Training Script"
                    fullWidth
                    margin="normal"
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    helperText="A name to help you identify this token later"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newLabel.trim()) handleCreate();
                    }}
                  />
                  <TextField
                    label="Expires in days (optional)"
                    placeholder="Leave blank for no expiry"
                    fullWidth
                    margin="normal"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={expiresInDays}
                    onChange={e => setExpiresInDays(e.target.value)}
                  />
                </>
              ) : (
                <>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={1} mt={1}>
                    <CheckCircleOutlineIcon
                      style={{ color: '#4caf50', fontSize: 20 }}
                    />
                    <Typography variant="body2" color="error">
                      <strong>Copy this token now</strong> — it will not be
                      shown again.
                    </Typography>
                  </Box>

                  {/* Token value */}
                  <div className={classes.tokenBox}>
                    <span style={{ flex: 1 }}>{newToken.token}</span>
                    <Tooltip title="Copy token">
                      <IconButton
                        size="small"
                        onClick={() =>
                          copy(newToken.token, 'Token copied!')
                        }
                        style={{ color: '#888' }}
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>

                  <Divider style={{ margin: '16px 0' }} />

                  {/* Ready-to-use Python snippet */}
                  <Typography variant="subtitle2" gutterBottom>
                    Ready-to-use Python snippet
                  </Typography>
                  <div className={classes.codeSnippet}>
                    {pythonSnippet(newToken.token)}
                    <Tooltip title="Copy snippet">
                      <IconButton
                        size="small"
                        className={classes.copySnippetBtn}
                        onClick={() =>
                          copy(
                            pythonSnippet(newToken.token),
                            'Snippet copied!',
                          )
                        }
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              {!newToken ? (
                <>
                  <Button
                    onClick={() => {
                      setCreateOpen(false);
                      setNewLabel('');
                      setExpiresInDays('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreate}
                    color="primary"
                    variant="contained"
                    disabled={creating || !newLabel.trim()}
                    startIcon={
                      creating ? (
                        <CircularProgress size={16} />
                      ) : (
                        <VpnKeyIcon />
                      )
                    }
                  >
                    Generate
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleFirstTokenDone}
                  color="primary"
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                >
                  Open MLflow
                </Button>
              )}
            </DialogActions>
          </Dialog>

          <Snackbar
            open={!!snackbar}
            message={snackbar}
            autoHideDuration={4000}
            onClose={() => setSnackbar(null)}
          />
        </Content>
      </Page>
    );
  }

  // ── Render: Manage Tokens panel ────────────────────────────────────────────
  if (view === 'tokens') {
    return (
      <Page themeId="tool">
        <Header title="AI Trainer" subtitle="MLflow Experiment Tracking">
          <HeaderLabel label="Environment" value="MLflow" />
        </Header>
        <Content>
          <Paper className={classes.tokenPanel}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="h6">Personal API Tokens</Typography>
              <Box display="flex" style={{ gap: 8 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setView('mlflow')}
                  startIcon={<OpenInNewIcon fontSize="small" />}
                >
                  Back to MLflow
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateOpen(true)}
                >
                  Generate Token
                </Button>
              </Box>
            </Box>

            <Typography variant="body2" color="textSecondary" gutterBottom>
              Tokens authenticate the MLflow Python SDK from scripts, notebooks,
              or CI pipelines. Tokens are shown <strong>once</strong> at
              creation — store them securely (e.g. in your shell profile or CI
              secrets).
            </Typography>

            {/* Generic usage snippet */}
            <Typography className={classes.sectionTitle} variant="subtitle2">
              Python usage
            </Typography>
            <div className={classes.codeSnippet} style={{ position: 'relative' }}>
              {genericSnippet}
              <Tooltip title="Copy snippet">
                <IconButton
                  size="small"
                  className={classes.copySnippetBtn}
                  onClick={() => copy(genericSnippet, 'Snippet copied!')}
                >
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>

            <Typography
              className={classes.sectionTitle}
              variant="subtitle2"
              style={{ marginTop: 24 }}
            >
              Your tokens
            </Typography>

            {tokens.length === 0 ? (
              <Typography color="textSecondary" style={{ padding: '16px 0' }}>
                No tokens yet. Click "Generate Token" to create one.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Label</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokens.map(t => (
                    <TableRow key={t.id} hover>
                      <TableCell>{t.label}</TableCell>
                      <TableCell>{formatDate(t.created_at)}</TableCell>
                      <TableCell>
                        {t.expires_at ? (
                          <Chip
                            size="small"
                            label={
                              isExpired(t.expires_at)
                                ? `Expired ${formatDate(t.expires_at)}`
                                : formatDate(t.expires_at)
                            }
                            color={
                              isExpired(t.expires_at) ? 'secondary' : 'default'
                            }
                          />
                        ) : (
                          <Chip size="small" label="Never" />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(t.last_used_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Revoke token">
                          <IconButton
                            size="small"
                            onClick={() => handleRevoke(t.id, t.label)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>

          {/* Create Token Dialog — manage panel variant */}
          <Dialog
            open={createOpen}
            onClose={() => {
              setCreateOpen(false);
              setNewLabel('');
              setExpiresInDays('');
              setNewToken(null);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {newToken ? 'Token Created' : 'Generate API Token'}
            </DialogTitle>
            <DialogContent>
              {!newToken ? (
                <>
                  <TextField
                    autoFocus
                    label="Token label"
                    placeholder="e.g. CI Pipeline"
                    fullWidth
                    margin="normal"
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newLabel.trim()) handleCreate();
                    }}
                  />
                  <TextField
                    label="Expires in days (optional)"
                    placeholder="Leave blank for no expiry"
                    fullWidth
                    margin="normal"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={expiresInDays}
                    onChange={e => setExpiresInDays(e.target.value)}
                  />
                </>
              ) : (
                <>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={1} mt={1}>
                    <CheckCircleOutlineIcon
                      style={{ color: '#4caf50', fontSize: 20 }}
                    />
                    <Typography variant="body2" color="error">
                      <strong>Copy this token now</strong> — it will not be
                      shown again.
                    </Typography>
                  </Box>
                  <div className={classes.tokenBox}>
                    <span style={{ flex: 1 }}>{newToken.token}</span>
                    <Tooltip title="Copy token">
                      <IconButton
                        size="small"
                        onClick={() =>
                          copy(newToken.token, 'Token copied!')
                        }
                        style={{ color: '#888' }}
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <Divider style={{ margin: '16px 0' }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Ready-to-use Python snippet
                  </Typography>
                  <div className={classes.codeSnippet} style={{ position: 'relative' }}>
                    {pythonSnippet(newToken.token)}
                    <Tooltip title="Copy snippet">
                      <IconButton
                        size="small"
                        className={classes.copySnippetBtn}
                        onClick={() =>
                          copy(
                            pythonSnippet(newToken.token),
                            'Snippet copied!',
                          )
                        }
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              {!newToken ? (
                <>
                  <Button
                    onClick={() => {
                      setCreateOpen(false);
                      setNewLabel('');
                      setExpiresInDays('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreate}
                    color="primary"
                    variant="contained"
                    disabled={creating || !newLabel.trim()}
                    startIcon={
                      creating ? (
                        <CircularProgress size={16} />
                      ) : (
                        <VpnKeyIcon />
                      )
                    }
                  >
                    Generate
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => {
                    setCreateOpen(false);
                    setNewLabel('');
                    setExpiresInDays('');
                    setNewToken(null);
                  }}
                  color="primary"
                  variant="contained"
                >
                  Done
                </Button>
              )}
            </DialogActions>
          </Dialog>

          <Snackbar
            open={!!snackbar}
            message={snackbar}
            autoHideDuration={4000}
            onClose={() => setSnackbar(null)}
          />
        </Content>
      </Page>
    );
  }

  // ── Render: MLflow iframe (main view) ──────────────────────────────────────
  return (
    <Page themeId="tool">
      <Header title="AI Trainer" subtitle="MLflow Experiment Tracking">
        <HeaderLabel label="Environment" value="MLflow" />
      </Header>
      <Content>
        <div className={classes.iframeRoot}>
          {/* Toolbar */}
          <div className={classes.toolbar}>
            <Typography className={classes.toolbarTitle}>
              MLflow Tracking Server
            </Typography>
            <Tooltip title="Manage API tokens for Python SDK access">
              <Button
                size="small"
                variant="outlined"
                startIcon={<VpnKeyIcon fontSize="small" />}
                onClick={() => {
                  fetchTokens();
                  setView('tokens');
                }}
              >
                API Tokens
              </Button>
            </Tooltip>
            {isOwner && (
              <Tooltip title="Permanently delete a soft-deleted MLflow experiment">
                <Button
                  size="small"
                  color="secondary"
                  variant="outlined"
                  startIcon={<DeleteIcon fontSize="small" />}
                  onClick={() => setPurgeOpen(true)}
                >
                  Purge Experiment
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Open MLflow in a new tab">
              <Button
                size="small"
                variant="text"
                href={mlflowUiUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon fontSize="small" />}
              >
                Full Screen
              </Button>
            </Tooltip>
          </div>

          {/* Expiry warning banner */}
          {showExpiryWarning && soonestExpiry && (
            <div className={classes.expiryBanner}>
              Your API token &quot;{soonestExpiry.label}&quot; expires in{' '}
              {expiryDays} day{expiryDays === 1 ? '' : 's'}.{' '}
              <Button
                size="small"
                style={{ marginLeft: 8 }}
                onClick={() => {
                  fetchTokens();
                  setView('tokens');
                }}
              >
                Manage Tokens
              </Button>
            </div>
          )}

          {/* MLflow iframe */}
          <iframe
            className={classes.iframe}
            src={mlflowUiUrl}
            title="MLflow Tracking Server"
            allow="fullscreen"
            sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          />
        </div>

        <Dialog
          open={purgeOpen}
          onClose={closePurgeDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Permanently Delete Experiment</DialogTitle>
          <DialogContent>
            <Typography color="error" variant="body2" gutterBottom>
              This cannot be undone. MLflow will permanently delete the selected
              soft-deleted experiment, its runs, metadata, and artifacts.
            </Typography>
            <TextField
              autoFocus
              label="Soft-deleted experiment ID"
              fullWidth
              margin="normal"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              value={purgeExperimentId}
              onChange={event => setPurgeExperimentId(event.target.value)}
              helperText="Find the experiment ID in the MLflow experiment URL."
            />
            <TextField
              label={`Type PURGE ${purgeExperimentId.trim() || '<experiment ID>'} to confirm`}
              fullWidth
              margin="normal"
              value={purgeConfirmation}
              onChange={event => setPurgeConfirmation(event.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closePurgeDialog} disabled={purging}>
              Cancel
            </Button>
            <Button
              color="secondary"
              variant="contained"
              disabled={
                purging ||
                !/^\d+$/.test(purgeExperimentId.trim()) ||
                purgeConfirmation !== `PURGE ${purgeExperimentId.trim()}`
              }
              onClick={handlePurgeExperiment}
              startIcon={purging ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              Permanently Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!snackbar}
          message={snackbar}
          autoHideDuration={4000}
          onClose={() => setSnackbar(null)}
        />
      </Content>
    </Page>
  );
};